/*! © 2026 Corone Laureae. Configurator implementation and visual assets: all rights reserved. */
(() => {
  if (customElements.get('cl-configurator')) return;

  class CoroneLaureaeConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.locale = this.dataset.locale || document.documentElement.lang || 'it';
      try {
        const data = JSON.parse(this.querySelector('[data-clc-i18n]')?.textContent || '{}');
        this.i18n = data && typeof data === 'object' ? data : {};
      } catch { this.i18n = {}; }
      this.renderChoiceLabels();

      this.stage = this.querySelector('[data-clc-stage]');
      this.threeDRoot = this.querySelector('[data-cl3d-root]');
      this.totalNode = this.querySelector('[data-clc-total]');
      this.statusNode = this.querySelector('[data-clc-status]');
      this.submitButton = this.querySelector('[data-clc-submit]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.submitDefaultLabel = this.submitLabel?.textContent || '';
      this.completionNote = this.querySelector('[data-clc-completion-note]');
      this.deliveryStatus = this.querySelector('[data-clc-delivery-status]');
      this.deliveryNational = this.querySelector('[data-clc-delivery-national]');
      this.deliveryLocal = this.querySelector('[data-clc-delivery-local]');
      this.localConfirm = this.querySelector('[data-clc-local-confirm]');
      this.localConfirmWrap = this.querySelector('[data-clc-local-confirm-wrap]');
      this.resetButton = this.querySelector('[data-clc-reset]');
      this.resetDialog = this.querySelector('[data-clc-reset-dialog]');
      this.resetCancel = this.querySelector('[data-clc-reset-cancel]');
      this.resetConfirm = this.querySelector('[data-clc-reset-confirm]');
      this.draftStatus = this.querySelector('[data-clc-draft-status]');
      this.cartCheckButton = this.querySelector('[data-clc-cart-check]');
      this.cartReviewLink = this.querySelector('[data-clc-cart-review]');
      this.previewColumn = this.querySelector('[data-clc-preview-column]');
      this.previewOpenButton = this.querySelector('[data-clc-preview-open]');
      this.previewCloseButton = this.querySelector('[data-clc-preview-close]');
      this.mobileBar = this.querySelector('[data-clc-mobile-bar]');
      this.mobileTotalNode = this.querySelector('[data-clc-mobile-total]');
      this.headerElement = document.querySelector('[id$="__header"], sticky-header, .shopify-section-header');
      this.syncPreviewStickyOffset = this.syncPreviewStickyOffset.bind(this);
      window.addEventListener?.('scroll', this.syncPreviewStickyOffset, { passive: true });
      window.addEventListener?.('resize', this.syncPreviewStickyOffset);
      if (this.headerElement && typeof ResizeObserver === 'function') {
        this.headerResizeObserver = new ResizeObserver(this.syncPreviewStickyOffset);
        this.headerResizeObserver.observe(this.headerElement);
      }
      this.ensureTopBabyBreathSlots();
      this.isSubmitting = false;
      this.isReconciling = false;
      this.cartAttempt = this.readCartAttempt();
      this.draftTrackingReady = false;
      this.suspendDraftSave = false;
      this.flushDraftOnPageHide = () => this.flushDraft();
      this.restoreCartControlsOnPageShow = event => {
        if (!event.persisted) return;
        this.isSubmitting = false;
        this.isReconciling = false;
        this.update();
      };
      window.addEventListener?.('pagehide', this.flushDraftOnPageHide);
      window.addEventListener?.('pageshow', this.restoreCartControlsOnPageShow);
      this.isPreviewOpen = false;
      this.textInputs = {
        left: this.querySelector('[data-clc-text-left]'),
        right: this.querySelector('[data-clc-text-right]'),
      };
      this.textPreviews = {
        left: this.querySelector('[data-clc-ribbon-text-left]'),
        right: this.querySelector('[data-clc-ribbon-text-right]'),
      };
      this.countNodes = {
        left: this.querySelector('[data-clc-count-left]'),
        right: this.querySelector('[data-clc-count-right]'),
      };
      this.customColorInput = this.querySelector('[data-clc-custom-color]');
      this.customColorWrap = this.querySelector('[data-clc-custom-color-wrap]');
      this.legacyLetteringNote = this.querySelector('[data-clc-lettering-legacy]');
      this.legacyLetteringValue = this.querySelector('[data-clc-lettering-legacy-value]');
      this.legacyLetteringColor = '';
      this.circumferenceInput = this.querySelector('[data-clc-circumference]');
      this.graduationDateInput = this.querySelector('[data-clc-graduation-date]');
      this.detailsInput = this.querySelector('[data-clc-details]');
      this.currency = this.dataset.currency || 'EUR';
      this.cartEnabled = this.dataset.cartEnabled === 'true';
      this.letteringUnitPrice = this.parseMoney(this.dataset.letteringPrice);
      this.money = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
      });
      this.roseCatalog = this.readRoseCatalog();
      this.legacyRoseColours = [];
      if (this.mixedRosesEnabled()) {
        try {
          const palette = JSON.parse(this.querySelector('[data-clc-legacy-rose-palette]')?.textContent || 'null');
          if (palette?.version === 'easify-colours-2026-10-10' && Array.isArray(palette.colours)) this.legacyRoseColours = palette.colours;
        } catch { this.legacyRoseColours = []; }
        for (const select of this.querySelectorAll('[data-clc-rose-color], [data-clc-bow-rose-color]')) {
          for (const colour of this.legacyRoseColours) {
            if ([...select.options].some(option => option.dataset.colorKey === colour.id)) continue;
            const option = document.createElement('option');
            option.value = colour.id;
            option.dataset.canonicalLabel = colour.label;
            option.textContent = this.displayChoice(colour.label);
            option.dataset.colorKey = colour.id;
            option.dataset.color = colour.color;
            option.dataset.image = '';
            select.append(option);
          }
        }
      }
      this.roseMixSelections = { roses: [], 'bow-roses': [] };
      this.disabledRoseMixDrafts = new Set();
      this.syncRoseOptions();
      this.textSegmenter = typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(this.locale, { granularity: 'grapheme' })
        : null;
      if (this.threeDRoot) {
        this.threeDRoot.cl3dGetState = () => this.threeDState(this.currentState || this.state());
      }
      this.addEventListener('change', (event) => this.handleChange(event));
      this.addEventListener('input', (event) => this.handleInput(event));
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.submitButton?.addEventListener('click', () => this.submit());
      this.resetButton?.addEventListener('click', () => this.resetConfiguration());
      this.resetCancel?.addEventListener('click', () => this.finishResetConfirmation(false));
      this.resetConfirm?.addEventListener('click', () => this.finishResetConfirmation(true));
      this.resetDialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.finishResetConfirmation(false);
      });
      this.resetDialog?.addEventListener('click', event => {
        if (event.target === this.resetDialog) this.finishResetConfirmation(false);
      });
      this.resetDialog?.addEventListener('close', () => this.finishResetConfirmation(false));
      this.cartCheckButton?.addEventListener('click', () => this.reconcileCartAttempt());
      this.previewOpenButton?.addEventListener('click', () => this.openPreview());
      this.previewCloseButton?.addEventListener('click', () => this.closePreview());
      this.previewColumn?.addEventListener('click', (event) => {
        if (this.isPreviewOpen && event.target === this.previewColumn) this.closePreview();
      });

      if (this.mobileBar) {
        this.previewInlineVisible = true;
        this.configuratorVisible = true;
        if ('IntersectionObserver' in window) {
          this.previewObserver = new IntersectionObserver(([entry]) => {
            if (this.isPreviewOpen) return;
            this.previewInlineVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.25);
            this.syncMobileBar();
          }, { threshold: [0, 0.25] });
          this.configuratorObserver = new IntersectionObserver(([entry]) => {
            this.configuratorVisible = Boolean(entry?.isIntersecting);
            this.syncMobileBar();
          });
          this.previewObserver.observe(this.previewColumn);
          this.configuratorObserver.observe(this);
        } else {
          this.mobileBar.hidden = false;
        }
      }

      this.querySelectorAll('[data-clc-addon-card]').forEach((card) => {
        const toggle = card.querySelector('[data-clc-addon]');
        card.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !toggle?.checked;
        });
      });

      this.restoreDraft();
      this.update();
      this.draftTrackingReady = true;
      this.syncCartAttemptControls();
      this.syncPreviewStickyOffset();
    }

    message(key, values = {}) {
      const template = this.i18n?.messages?.[key];
      if (typeof template !== 'string') return '';
      return template.replace(/__([A-Z_]+)__/g, (token, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token);
    }

    canonicalLabel(option) {
      return option?.dataset?.canonicalLabel ?? option?.textContent?.trim() ?? '';
    }

    displayChoice(canonical) {
      const label = String(canonical || '');
      const choices = this.i18n?.choices || {};
      if (Object.prototype.hasOwnProperty.call(choices, label)) return choices[label];
      if (label.includes(' · ')) return label.split(' · ').map(part => this.displayChoice(part)).join(' · ');
      if (label.includes(' + ')) return label.split(' + ').map(part => this.displayChoice(part)).join(' + ');
      const roses = label.match(/^(\d+) rose$/);
      if (roses) return this.message('rose_count', { COUNT: roses[1] }) || label;
      const sunflowers = label.match(/^Girasoli: (\d+)$/);
      if (sunflowers) return this.message('sunflower_count', { COUNT: sunflowers[1] }) || label;
      const flowers = label.match(/^(\d+) fiori$/);
      if (flowers) return this.message('flowers_count', { COUNT: flowers[1] }) || label;
      const composition = label.match(/^(Piccola|Media)\s*\/\s*(\d+)$/);
      if (composition) return `${this.displayChoice(composition[1])} / ${composition[2]}`;
      const countedColour = label.match(/^(\d+) (.+)$/);
      if (countedColour && Object.prototype.hasOwnProperty.call(choices, countedColour[2])) {
        return `${countedColour[1]} ${choices[countedColour[2]]}`;
      }
      return label;
    }

    renderChoiceLabels() {
      // Only explicit, trusted option/choice display targets within this component.
      // Never replace arbitrary DOM, user-entered text, values, IDs or catalogue data.
      for (const node of this.querySelectorAll('option[data-canonical-label], [data-clc-choice-label]')) {
        const canonical = this.canonicalLabel(node);
        node.dataset.canonicalLabel = canonical;
        node.textContent = [this.displayChoice(canonical), node.dataset.displayPrice || ''].filter(Boolean).join(' · ');
        if (node.matches('[data-clc-choice-label]')) node.closest('[title]')?.setAttribute('title', node.textContent);
      }
    }

    displayAddon(addon) {
      const title = this.displayChoice(addon.title);
      return addon.detailLabel ? `${title} (${this.displayChoice(addon.detailLabel)})` : title;
    }

    ensureTopBabyBreathSlots() {
      const layer = this.querySelector('.clc__slot-layer--baby-breath[data-clc-layer="baby-breath"]');
      if (!layer) return;
      const count = layer.querySelectorAll('.clc__baby-slot').length;
      for (let index = count + 1; index <= 20; index += 1) {
        const piece = document.createElement('span');
        piece.className = `clc__slot-piece clc__baby-slot clc__baby-slot--${index}`;
        layer.append(piece);
      }
    }

    disconnectedCallback() {
      this.flushDraft();
      window.removeEventListener?.('pagehide', this.flushDraftOnPageHide);
      window.removeEventListener?.('pageshow', this.restoreCartControlsOnPageShow);
      this.finishResetConfirmation(false);
      this.previewObserver?.disconnect();
      this.configuratorObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      window.removeEventListener?.('scroll', this.syncPreviewStickyOffset);
      window.removeEventListener?.('resize', this.syncPreviewStickyOffset);
      if (!this.isPreviewOpen) return;
      this.isPreviewOpen = false;
      document.documentElement.classList.remove('clc-preview-modal-open');
      this.restorePreviewBackground();
    }

    syncPreviewStickyOffset() {
      if (!this.previewColumn) return;
      if (this.isPreviewOpen && window.matchMedia?.('(min-width: 750px)')?.matches) {
        this.closePreview({ focusTarget: this.previewColumn });
      }
      const desktop = window.matchMedia?.('(min-width: 990px)')?.matches;
      if (!desktop) {
        this.style.removeProperty('--clc-sticky-header-offset');
        return;
      }

      const header = this.headerElement
        || document.querySelector('[id$="__header"], sticky-header, .shopify-section-header');
      const bottom = Number(header?.getBoundingClientRect?.().bottom);
      const offset = Number.isFinite(bottom) && bottom > 0 ? Math.ceil(bottom) : 0;
      this.style.setProperty('--clc-sticky-header-offset', `${offset}px`);
    }

    handleChange(event) {
      const target = event.target;

      if (target.matches('[data-clc-lettering-color]')) this.legacyLetteringColor = '';

      if (target.matches('[data-clc-addon]')) {
        if (!target.checked) this.disabledRoseMixDrafts?.delete(target.dataset.layer);
        const card = target.closest('[data-clc-addon-card]');
        card?.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !target.checked;
        });
        if (target.checked) this.enforceBowAlternative(target.dataset.layer);
      }

      if (target.matches('[data-clc-local-confirm]') && this.statusNode) {
        this.statusNode.dataset.kind = '';
      }

      this.update();
    }

    enforceBowAlternative(preferredLayer) {
      const rose = this.querySelector('[data-clc-addon][data-layer="bow-roses"]');
      const sunflower = this.querySelector('[data-clc-addon][data-layer="bow-sunflower"]');
      if (!rose?.checked || !sunflower?.checked) return;
      const discarded = preferredLayer === 'bow-roses' ? sunflower : rose;
      discarded.checked = false;
      discarded.closest('[data-clc-addon-card]')?.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach(field => { field.disabled = true; });
    }

    handleInput(event) {
      if (event.target.matches('[data-clc-custom-color]')) {
        this.legacyLetteringColor = '';
        const value = event.target.value.slice(0, 36);
        if (event.target.value !== value) event.target.value = value;
        event.target.setCustomValidity('');
        this.update();
        return;
      }

      if (event.target.matches('[data-clc-circumference], [data-clc-graduation-date], [data-clc-details]')) {
        event.target.setCustomValidity('');
        this.update();
        return;
      }

      if (!event.target.matches('[data-clc-text-left], [data-clc-text-right]')) return;
      const side = event.target.matches('[data-clc-text-left]') ? 'left' : 'right';
      const value = this.truncateText(event.target.value, 28);
      if (event.target.value !== value) event.target.value = value;
      if (this.countNodes[side]) this.countNodes[side].textContent = String(this.textLength(value));
      if (this.textPreviews[side]) this.textPreviews[side].textContent = value;
      this.update();
    }

    handleKeydown(event) {
      if (!this.isPreviewOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePreview();
        return;
      }

      if (event.key !== 'Tab' || !this.previewColumn) return;
      const focusable = [...this.previewColumn.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((element) => element.tabIndex >= 0 && !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    syncMobileBar() {
      if (!this.mobileBar) return;
      this.mobileBar.hidden = this.isPreviewOpen || !this.configuratorVisible || this.previewInlineVisible;
    }

    completionRequirements(state) {
      const missing = [];
      if (!state.circumference?.trim()) missing.push(this.message('missing_circumference'));
      if (!state.graduationDate?.trim()) missing.push(this.message('missing_graduation'));
      if (state.letteringColor === 'Extra' && !state.customLetteringColor?.trim()) {
        missing.push(this.message('missing_extra'));
      }
      if (state.hasLettering && !/^\d+$/.test(String(state.letteringVariantId || ''))) {
        missing.push(this.dataset.letteringMissingLabel);
      }
      if (state.requiresLocalDelivery && !this.localConfirm?.checked) {
        missing.push(this.message('missing_local'));
      }
      return missing;
    }

    formatRequirements(requirements) {
      if (requirements.length <= 1) return requirements[0] || '';
      return `${requirements.slice(0, -1).join(', ')} ${this.message('and')} ${requirements.at(-1)}`;
    }

    checked(selector) {
      return this.querySelector(`${selector}:checked`);
    }

    parseMoney(value) {
      const parsed = Number.parseInt(String(value || '0'), 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    formatMoney(cents) {
      return this.money.format(cents / 100);
    }

    textParts(value) {
      const text = String(value || '');
      if (this.textSegmenter) {
        return Array.from(this.textSegmenter.segment(text), (part) => part.segment);
      }
      return Array.from(text);
    }

    truncateText(value, limit) {
      return this.textParts(value).slice(0, limit).join('');
    }

    textLength(value) {
      return this.textParts(value).length;
    }

    contrastFor(hex) {
      const normalized = String(hex || '').replace('#', '').trim();
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return '#fff8e8';
      const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255);
      const linear = channels.map((value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
      const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
      return luminance > 0.52 ? '#302b25' : '#fff8e8';
    }

    addonState() {
      return [...this.querySelectorAll('[data-clc-addon]:checked')].map((toggle) => {
        const card = toggle.closest('[data-clc-addon-card]');
        const select = card?.querySelector('[data-clc-addon-variant]');
        const option = select?.selectedOptions?.[0];
        const roseColorSelect = card?.querySelector('[data-clc-rose-color]');
        const roseColorOption = roseColorSelect?.selectedOptions?.[0];
        const bowRoseColorSelect = card?.querySelector('[data-clc-bow-rose-color]');
        const bowRoseColorOption = bowRoseColorSelect?.selectedOptions?.[0];
        const babyColorSelect = card?.querySelector('[data-clc-baby-color]');
        const babyColorOption = babyColorSelect?.selectedOptions?.[0];
        const isBabyBreath = toggle.dataset.layer === 'baby-breath';
        const isRoses = toggle.dataset.layer === 'roses';
        const isBowRoses = toggle.dataset.layer === 'bow-roses';
        const roseCount = option?.dataset.roseCount || '3';
        const roseSizeSelect = card?.querySelector('[data-clc-rose-size]');
        const roseSize = this.normaliseRoseSize(roseSizeSelect?.value);
        const roseColorLabel = this.canonicalLabel(roseColorOption);
        const roseVariant = isRoses ? this.resolveRoseVariant(roseColorLabel, roseCount, roseSize) : null;
        const roseMode = this.disabledRoseMixDrafts?.has(toggle.dataset.layer) ? 'mix'
          : (isRoses || isBowRoses) ? card?.querySelector('[data-clc-rose-mode]')?.value || 'uniform' : 'uniform';
        const mixed = roseMode === 'mix';
        const flowerCount = Number(isRoses ? roseCount : option?.dataset.bowRoseCount || '1');
        const flowerDimension = isRoses ? roseSize : this.normaliseRoseSize(option?.dataset.flowerSize);
        const roseSelection = (isRoses || isBowRoses) ? this.roseSelection(card, flowerCount, flowerDimension, mixed, isBowRoses) : null;
        const commercialRoseVariant = isRoses && mixed ? this.resolveRoseVariant('Mix di colori', roseCount, roseSize) : roseVariant;
        const variantId = isBabyBreath
          ? babyColorOption?.value || toggle.dataset.variantId || ''
          : isRoses
            ? commercialRoseVariant?.available === true ? String(commercialRoseVariant.id) : ''
          : option?.value || toggle.dataset.variantId || '';
        const price = this.parseMoney(
          isBabyBreath
            ? babyColorOption?.dataset.price || toggle.dataset.price
            : isRoses ? commercialRoseVariant?.price
            : option?.dataset.price || toggle.dataset.price,
        );
        const variantLabel = this.canonicalLabel(option);
        const bowRoseColorLabel = this.canonicalLabel(bowRoseColorOption);
        const babyColorLabel = this.canonicalLabel(babyColorOption);
        const babyMode = card?.querySelector('[data-clc-baby-mode]')?.value || 'a-ciuffi';
        const babyColorKey = babyColorOption?.dataset.colorKey || '';
        const babyImage = babyColorOption?.dataset.image || '';
        const colorKey = mixed ? roseSelection?.roseColors?.[0]?.colorKey || '' : isBowRoses
          ? bowRoseColorOption?.dataset.colorKey || ''
          : roseColorOption?.dataset.colorKey || option?.dataset.colorKey || '';
        const image = mixed ? roseSelection?.roseColors?.[0]?.imageUrl || '' : isBowRoses
          ? bowRoseColorOption?.dataset.image || ''
          : roseColorOption?.dataset.image || option?.dataset.image || '';
        const color = mixed ? roseSelection?.roseColors?.[0]?.color || '' : bowRoseColorOption?.dataset.color
          || roseColorOption?.dataset.color
          || babyColorOption?.dataset.color
          || option?.dataset.color
          || toggle.dataset.color
          || '';
        const imageUrl = isBabyBreath ? babyImage : isRoses || isBowRoses ? image : '';
        const mode = option?.dataset.mode || '';
        const level = option?.dataset.level || 'delicata';
        const bowRoseCount = option?.dataset.bowRoseCount || '1';
        const flowerSize = option?.dataset.flowerSize || 'piccola';
        const cartQuantity = isRoses ? 1 : Number.parseInt(option?.dataset.cartQuantity || toggle.dataset.cartQuantity || '1', 10) || 1;
        const cleanVariantLabel = variantLabel.replace(/\s*·.*$/, '');
        let detailLabel = cleanVariantLabel === 'Default Title' ? '' : cleanVariantLabel;

        if (toggle.dataset.layer === 'baby-breath') {
          detailLabel = `${babyMode === 'sparsa' ? 'sparsa' : 'a ciuffi'}${babyColorLabel ? ` · ${babyColorLabel}` : ''}`;
        }
        if (isRoses) detailLabel = [`${roseCount} rose`, this.canonicalLabel(roseSizeSelect?.selectedOptions?.[0]), roseColorLabel].filter(Boolean).join(' · ');
        if (toggle.dataset.layer === 'bow-roses') detailLabel = `${cleanVariantLabel}${bowRoseColorLabel ? ` · ${bowRoseColorLabel}` : ''}`;
        if (mixed) detailLabel = [isRoses ? `${roseCount} rose` : cleanVariantLabel, isRoses ? this.canonicalLabel(roseSizeSelect?.selectedOptions?.[0]) : '', this.roseMixSummary(roseSelection?.rose_mix, card)].filter(Boolean).join(' · ');
        if (toggle.dataset.layer === 'gardenia') detailLabel = `3 fiori${detailLabel ? ` · ${detailLabel}` : ''}`;
        if (['hydrangea', 'lagurus', 'berries', 'hypericum'].includes(toggle.dataset.layer)) {
          detailLabel = [this.dataset.fullCompositionLabel, detailLabel].filter(Boolean).join(' · ');
        }

        return {
          layer: toggle.dataset.layer || '',
          title: toggle.dataset.title || 'Accessorio',
          variantId,
          variantLabel,
          detailLabel,
          price,
          color,
          imageUrl,
          babyMode,
          babyColorKey,
          babyImage,
          roseCount,
          roseSize: isRoses ? roseSize : undefined,
          catalogAvailable: isRoses ? commercialRoseVariant?.available === true && (!mixed || this.mixedRosesEnabled() && roseSelection?.valid === true && roseSelection.prices.every(value => value === commercialRoseVariant.price))
            : isBowRoses ? Boolean(option && !option.disabled && (mixed ? this.mixedRosesEnabled() && flowerCount === 3 && roseSelection?.valid === true : bowRoseColorOption && !bowRoseColorOption.disabled))
            : toggle.dataset.layer === 'sunflower' || toggle.dataset.layer === 'bow-sunflower'
              ? (option?.dataset.available || toggle.dataset.available) === 'true' : true,
          colorKey,
          image,
          mode,
          level,
          bowRoseCount,
          flowerSize,
          cartQuantity,
          ...(mixed ? { roseMode: 'mix', roseColors: roseSelection?.roseColors || [], rose_mix: roseSelection?.rose_mix || [] } : {}),
          localOnly: toggle.dataset.localOnly === 'true',
        };
      });
    }

    normaliseRoseSize(value) {
      return String(value || '').toLowerCase() === 'piccola' ? 'piccola' : 'media';
    }

    readRoseCatalog() {
      try {
        const variants = JSON.parse(this.querySelector('[data-clc-rose-catalog]')?.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch (_error) {
        return [];
      }
    }

    resolveRoseVariant(color, count, size) {
      const normalizedSize = this.normaliseRoseSize(size);
      const allowedCounts = normalizedSize === 'piccola' ? ['5', '8', '10'] : ['3', '5', '8'];
      if (!allowedCounts.includes(String(count))) return null;
      const matches = (this.roseCatalog || []).filter((variant) => {
        if (!variant || !Array.isArray(variant.options) || !/^\d+$/.test(String(variant.id || ''))
          || !Number.isSafeInteger(variant.price) || variant.price <= 0 || typeof variant.available !== 'boolean') return false;
        const rawSize = String(variant.options[2] || '').trim().toLowerCase();
        if (!['', 'media', 'grande', 'piccola'].includes(rawSize)) return false;
        const rawCount = String(variant.options[1] || '').trim().match(/^(3|5|8|10)(?:\s+rose)?$/i);
        return variant.options[0] === color && rawCount?.[1] === String(count)
          && this.normaliseRoseSize(rawSize) === normalizedSize;
      });
      return matches.length === 1 ? matches[0] : null;
    }

    roseCountLabel(count) {
      return String(this.dataset.roseCountLabel || '__COUNT__').replace('__COUNT__', String(count));
    }

    mixedRosesEnabled() {
      return this.dataset.mixedRosesEnabled === 'true';
    }

    isLegacyRoseColour(id, label) {
      return this.mixedRosesEnabled() && (this.legacyRoseColours || []).some(colour => colour.id === id && colour.label === label);
    }

    roseSelection(card, count, size, mixed, bow = false) {
      const source = card?.querySelector(bow ? '[data-clc-bow-rose-color]' : '[data-clc-rose-color]');
      const keys = mixed ? [...card?.querySelectorAll('[data-clc-rose-each-color]') || []].map(field => field.value) : Array(count).fill(source?.value || '');
      const colors = keys.map(key => [...source?.options || []].find(option => option.dataset.colorKey === key || option.value === key));
      const available = option => {
        if (!option) return null;
        if (mixed && this.isLegacyRoseColour(option.dataset.colorKey, this.canonicalLabel(option))) {
          if (!bow) return this.resolveRoseVariant('Mix di colori', count, size);
          const composition = card.querySelector('[data-clc-addon-variant]')?.selectedOptions?.[0];
          return composition && !composition.disabled && Number(composition.dataset.bowRoseCount) === count
            && this.normaliseRoseSize(composition.dataset.flowerSize) === size
            ? { available: true, price: Number(composition.dataset.price) } : null;
        }
        if (!bow) return this.resolveRoseVariant(this.canonicalLabel(option), count, size);
        return (this.roseCatalog || []).find(variant => variant.available === true && variant.options?.[0] === this.canonicalLabel(option) && this.normaliseRoseSize(variant.options?.[2]) === size);
      };
      const variants = colors.map(available);
      const palette = { rosso: '#c61d30', 'bianco-optical': '#fffef9', 'bianco-avorio': '#f5eed2', rosa: '#ee71ab', 'rosa-antico': '#b95f7c', blu: '#173ed0', viola: '#842594', giallo: '#edc330' };
      const roseColors = colors.map(option => ({ colour_id: option?.dataset.colorKey || '', colorKey: option?.dataset.colorKey || '', label: this.canonicalLabel(option), imageUrl: option?.dataset.image || '', image: option?.dataset.image || '', color: option?.dataset.color || palette[option?.dataset.colorKey] || '' }));
      const counts = new Map();
      for (const entry of roseColors) counts.set(entry.colour_id, (counts.get(entry.colour_id) || 0) + 1);
      const rose_mix = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b, 'en')).map(([colour_id, amount]) => ({ colour_id, count: amount }));
      return { valid: Number.isInteger(count) && count > 0 && keys.length === count && colors.every(Boolean) && variants.every(variant => variant?.available === true), roseColors, rose_mix, prices: variants.map(variant => variant?.price) };
    }

    roseMixSummary(allocation, card) {
      const source = card?.querySelector('[data-clc-rose-color], [data-clc-bow-rose-color]');
      return (allocation || []).map(entry => {
        const option = [...source?.options || []].find(candidate => candidate.dataset.colorKey === entry.colour_id);
        return `${entry.count} ${this.canonicalLabel(option) || entry.colour_id}`;
      }).join(' + ');
    }

    syncRoseMixFields(card, count, size, bow = false) {
      if (!card) return;
      const layer = bow ? 'bow-roses' : 'roses';
      const mode = card.querySelector('[data-clc-rose-mode]');
      const source = card.querySelector(bow ? '[data-clc-bow-rose-color]' : '[data-clc-rose-color]');
      const container = card.querySelector('[data-clc-rose-mix-fields]');
      if (!mode || !source || !container) return;
      this.roseMixSelections ||= { roses: [], 'bow-roses': [] };
      const oldFields = [...container.querySelectorAll('[data-clc-rose-each-color]')];
      if (oldFields.length) this.roseMixSelections[layer] = oldFields.map(field => field.value);
      if (bow && count !== 3 && mode.value === 'mix') mode.value = 'uniform';
      const mixed = mode.value === 'mix';
      container.hidden = !mixed || !this.mixedRosesEnabled();
      source.closest('label').hidden = mixed && this.mixedRosesEnabled();
      source.disabled = !card.querySelector('[data-clc-addon]')?.checked || mixed && this.mixedRosesEnabled();
      mode.disabled = !card.querySelector('[data-clc-addon]')?.checked || !this.mixedRosesEnabled();
      const mixOption = [...mode.options].find(option => option.value === 'mix');
      const availableMix = this.mixedRosesEnabled() && (bow ? count === 3 : this.resolveRoseVariant('Mix di colori', count, size)?.available === true);
      if (mixOption) mixOption.disabled = !availableMix;
      const signature = `${count}|${size}`;
      if (container.dataset.signature !== signature) {
        const fields = Array.from({ length: count }, (_unused, index) => {
          const label = document.createElement('label');
          label.className = 'clc__addon-variant';
          const caption = document.createElement('span');
          caption.textContent = String(this.dataset.roseEachColourLabel || '__INDEX__').replace('__INDEX__', String(index + 1));
          const field = document.createElement('select');
          field.dataset.clcRoseEachColor = '';
          field.dataset.clcDependent = '';
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = this.message('choose_colour');
          field.append(placeholder);
          for (const option of source.options) field.append(option.cloneNode(true));
          field.value = index < this.roseMixSelections[layer].length ? this.roseMixSelections[layer][index] : '';
          label.append(caption, field);
          return label;
        });
        container.replaceChildren(...fields);
        container.dataset.signature = signature;
      }
      for (const field of container.querySelectorAll('[data-clc-rose-each-color]')) {
        field.disabled = !card.querySelector('[data-clc-addon]')?.checked || !mixed || !this.mixedRosesEnabled();
        for (const option of field.options) {
          if (option.value === '') { option.disabled = false; continue; }
          const variant = bow ? (this.roseCatalog || []).find(item => item.available === true && item.options?.[0] === this.canonicalLabel(option) && this.normaliseRoseSize(item.options?.[2]) === size) : this.resolveRoseVariant(this.canonicalLabel(option), count, size);
          option.disabled = variant?.available !== true && !this.isLegacyRoseColour(option.dataset.colorKey, this.canonicalLabel(option));
        }
        if (field.selectedOptions?.[0]?.disabled) field.value = '';
      }
    }

    syncRoseOptions(preferredCount) {
      const sizeSelect = this.querySelector('[data-clc-rose-size]');
      const colorSelect = this.querySelector('[data-clc-rose-color]');
      const card = sizeSelect?.closest('[data-clc-addon-card]');
      const countSelect = card?.querySelector('[data-clc-addon-variant]');
      if (!sizeSelect || !colorSelect || !countSelect) return;
      const color = this.canonicalLabel(colorSelect.selectedOptions?.[0]);
      const mixed = card.querySelector('[data-clc-rose-mode]')?.value === 'mix';
      const validColor = (label, dimension) => (this.roseCatalog || []).some(variant => variant.options?.[0] === label
        && this.normaliseRoseSize(variant.options?.[2]) === dimension && variant.available === true);
      for (const option of sizeSelect.options) option.disabled = mixed ? !validColor('Mix di colori', option.value) : !validColor(color, option.value);
      if (!mixed && sizeSelect.selectedOptions?.[0]?.disabled) sizeSelect.value = [...sizeSelect.options].find(option => !option.disabled)?.value || sizeSelect.value;
      const availableSize = this.normaliseRoseSize(sizeSelect.value);
      for (const option of colorSelect.options) option.disabled = !validColor(this.canonicalLabel(option), availableSize);
      const counts = availableSize === 'piccola' ? ['5', '8', '10'] : ['3', '5', '8'];
      const requestedCount = String(preferredCount ?? countSelect.value ?? '');
      const selectedCount = counts.includes(requestedCount) ? requestedCount : counts[0];
      const choices = counts.map((count) => {
        const variant = this.resolveRoseVariant(mixed ? 'Mix di colori' : color, count, availableSize);
        const option = document.createElement('option');
        option.value = count;
        option.dataset.roseCount = count;
        option.dataset.canonicalLabel = `${count} rose`;
        option.disabled = variant?.available !== true;
        option.textContent = [this.roseCountLabel(count), variant ? this.formatMoney(variant.price) : '', option.disabled ? this.dataset.roseUnavailableLabel : ''].filter(Boolean).join(' · ');
        option.defaultSelected = count === counts[0];
        return option;
      });
      countSelect.replaceChildren(...choices);
      countSelect.value = selectedCount;
      this.syncRoseMixFields(card, Number(selectedCount), availableSize);
    }

    syncBowRoseColors() {
      const colorSelect = this.querySelector('[data-clc-bow-rose-color]');
      const composition = colorSelect?.closest('[data-clc-addon-card]')?.querySelector('[data-clc-addon-variant]');
      if (!colorSelect || !composition) return;
      const size = this.normaliseRoseSize(composition.selectedOptions?.[0]?.dataset.flowerSize);
      for (const option of colorSelect.options) {
        option.disabled = !this.isLegacyRoseColour(option.dataset.colorKey, this.canonicalLabel(option)) && !(this.roseCatalog || []).some(variant => variant.options?.[0] === this.canonicalLabel(option)
          && this.normaliseRoseSize(variant.options?.[2]) === size && variant.available === true);
      }
      if (colorSelect.selectedOptions?.[0]?.disabled) colorSelect.value = [...colorSelect.options].find(option => !option.disabled)?.value || '';
      this.syncRoseMixFields(colorSelect.closest('[data-clc-addon-card]'), Number(composition.selectedOptions?.[0]?.dataset.bowRoseCount || 1), size, true);
    }

    state() {
      const ribbon = this.checked('[data-clc-ribbon]');
      const bowStyle = this.checked('[data-clc-bow-style]');
      const width = this.checked('[data-clc-width]');
      const bands = this.checked('[data-clc-bands]');
      const lettering = this.checked('[data-clc-lettering-color]');
      const addons = this.addonState();
      const textLeft = this.textInputs.left?.value.trim() || '';
      const textRight = this.textInputs.right?.value.trim() || '';
      const customLetteringColor = this.customColorInput?.value.trim() || '';
      const circumference = this.circumferenceInput?.value.trim() || '';
      const graduationDate = this.graduationDateInput?.value.trim() || '';
      const details = this.detailsInput?.value.trim() || '';
      const basePrice = this.parseMoney(ribbon?.dataset.price);
      const bandsPrice = this.parseMoney(bands?.dataset.price);
      const addonsPrice = addons.reduce((sum, addon) => sum + addon.price, 0);
      const hasLettering = Boolean(textLeft || textRight);
      const letteringPrice = hasLettering ? this.letteringUnitPrice : 0;

      return {
        ribbon: ribbon?.value || '',
        ribbonColor: ribbon?.dataset.swatch || '#b31f2b',
        baseVariantId: ribbon?.dataset.variantId || '',
        basePrice,
        bowStyle: bowStyle?.value || 'farfalla',
        cut: 'punta',
        width: width?.value || 'largo',
        bands: bands?.value || '2',
        bandsVariantId: bands?.dataset.variantId || '',
        bandsPrice,
        addons,
        textLeft,
        textRight,
        letteringColor: lettering?.value || '',
        letteringLabel: lettering?.dataset.label || lettering?.value || '',
        letteringHex: lettering?.dataset.color || '#fffaf0',
        letteringVariantId: lettering && !lettering.disabled ? lettering.dataset.variantId || '' : '',
        letteringPrice,
        hasLettering,
        customLetteringColor,
        circumference,
        graduationDate,
        details,
        requiresLocalDelivery: addons.some((addon) => addon.localOnly),
        total: basePrice + bandsPrice + addonsPrice + letteringPrice,
      };
    }

    threeDState(state) {
      const items = state.addons.map((addon) => ({
        id: addon.layer,
        label: `${addon.title}${addon.detailLabel ? ` ${addon.detailLabel}` : ''}`,
        type: addon.layer,
        imageUrl: addon.layer === 'baby-breath' || addon.layer === 'roses' || addon.layer === 'bow-roses'
          ? String(addon.imageUrl || addon.babyImage || addon.image || '').trim()
          : '',
        colorKey: String(
          addon.layer === 'baby-breath'
            ? addon.babyColorKey || addon.colorKey || ''
            : addon.colorKey || addon.babyColorKey || '',
        ).trim(),
        color: /^#[0-9a-f]{6}$/i.test(addon.color || '') ? addon.color : '',
        quantity: Number.parseInt(
          addon.layer === 'roses'
            ? addon.roseCount
            : addon.layer === 'bow-roses'
              ? addon.bowRoseCount
              : addon.cartQuantity || '1',
          10,
        ) || 1,
        mode: addon.layer === 'baby-breath' ? addon.babyMode : addon.mode || '',
        roseSize: addon.layer === 'roses' ? this.normaliseRoseSize(addon.roseSize) : undefined,
        flowerSize: addon.flowerSize || 'piccola',
        localOnly: addon.localOnly,
        ...(addon.roseMode === 'mix' ? { roseColors: addon.roseColors.map(entry => ({ ...entry })) } : {}),
      }));

      return {
        ribbonColor: state.ribbonColor,
        ribbonColorName: state.ribbon,
        bowShape: state.bowStyle,
        ribbonWidth: state.width,
        bands: Number.parseInt(state.bands, 10) || 2,
        stabilized: items.filter((item) => !item.localOnly),
        fresh: items.filter((item) => item.localOnly),
        textLeft: state.textLeft,
        textRight: state.textRight,
        letteringHex: state.letteringHex,
      };
    }

    update() {
      this.enforceBowAlternative();
      this.syncRoseOptions();
      this.syncBowRoseColors();
      const state = this.state();
      this.currentState = state;

      this.stage.style.setProperty('--clc-ribbon', state.ribbonColor);
      this.stage.style.setProperty('--clc-ribbon-ink', this.contrastFor(state.ribbonColor));
      this.stage.style.setProperty('--clc-lettering', state.letteringHex);
      const usesCustomLettering = state.letteringColor === 'Extra';
      if (this.customColorWrap) this.customColorWrap.hidden = !usesCustomLettering;
      if (this.customColorInput) {
        this.customColorInput.disabled = !usesCustomLettering;
        if (!usesCustomLettering) this.customColorInput.setCustomValidity('');
      }
      if (this.legacyLetteringNote) this.legacyLetteringNote.hidden = !this.legacyLetteringColor;
      if (this.legacyLetteringValue) this.legacyLetteringValue.textContent = this.legacyLetteringColor || '';
      this.stage.dataset.cut = state.cut;
      this.stage.dataset.bowStyle = state.bowStyle;
      this.stage.dataset.width = state.width;
      this.stage.dataset.bands = state.bands;

      [
        'baby-breath',
        'fresh-baby-breath',
        'berries',
        'hypericum',
        'roses',
        'gardenia',
        'hydrangea',
        'lagurus',
        'bow-roses',
        'sunflower',
        'bow-sunflower',
      ].forEach((layer) => {
        this.stage.dataset[this.dataKey(`layer-${layer}`)] = String(state.addons.some((addon) => addon.layer === layer));
      });

      const berries = state.addons.find((addon) => addon.layer === 'berries');
      if (berries?.color) this.stage.style.setProperty('--clc-berry', berries.color);
      this.stage.dataset.berriesColor = berries?.variantLabel.toLocaleLowerCase('it-IT').includes('oro') ? 'oro' : 'rosso';

      const babyBreath = state.addons.find((addon) => addon.layer === 'baby-breath');
      this.stage.dataset.babyMode = babyBreath?.babyMode || 'a-ciuffi';
      this.stage.dataset.babyColor = babyBreath?.babyColorKey || 'bianco';
      if (babyBreath?.babyImage) this.stage.style.setProperty('--clc-active-baby-image', `url("${babyBreath.babyImage}")`);

      const babyColorOption = this.querySelector('[data-clc-baby-color]')?.selectedOptions?.[0];
      const babyVisual = this.querySelector('[data-clc-baby-visual]');
      if (babyVisual && babyColorOption?.dataset.image) {
        babyVisual.style.setProperty('--clc-addon-image', `url("${babyColorOption.dataset.image}")`);
      }

      const freshBabyBreath = state.addons.find((addon) => addon.layer === 'fresh-baby-breath');
      this.stage.dataset.freshBabyMode = freshBabyBreath?.mode || 'sparsa';

      const roses = state.addons.find((addon) => addon.layer === 'roses');
      const sunflower = state.addons.find((addon) => addon.layer === 'sunflower');
      this.stage.dataset.sunflowerCount = String(sunflower?.cartQuantity || 1);
      this.stage.dataset.roseCount = roses?.roseCount || '3';
      this.stage.dataset.roseSize = roses?.roseSize || 'media';
      this.stage.dataset.roseColor = roses?.colorKey || 'rosso';
      this.applyRoseSlotColors('.clc__rose-slot', roses);
      if ((roses?.colorKey || 'rosso') === 'rosso') {
        this.stage.style.setProperty('--clc-active-rose-image', 'var(--clc-generated-rose-red-image)');
      } else if (roses?.image) {
        this.stage.style.setProperty('--clc-active-rose-image', `url("${roses.image}")`);
      }

      const roseColorOption = this.querySelector('[data-clc-rose-color]')?.selectedOptions?.[0];
      const roseVisual = this.querySelector('[data-clc-rose-visual]');
      if (roseVisual && (roseColorOption?.dataset.photo || roseColorOption?.dataset.image)) {
        roseVisual.style.setProperty('--clc-addon-image', `url("${roseColorOption.dataset.photo || roseColorOption.dataset.image}")`);
      }

      const gardenia = state.addons.find((addon) => addon.layer === 'gardenia');
      this.stage.dataset.gardeniaColor = gardenia?.colorKey || 'rosa-antico';

      const hydrangea = state.addons.find((addon) => addon.layer === 'hydrangea');
      this.stage.dataset.hydrangeaColor = hydrangea?.colorKey || 'bianca';
      const hydrangeaOption = this.querySelector('[data-clc-addon][data-layer="hydrangea"]')
        ?.closest('[data-clc-addon-card]')
        ?.querySelector('[data-clc-addon-variant]')
        ?.selectedOptions?.[0];
      const hydrangeaVisual = this.querySelector('[data-clc-hydrangea-visual]');
      if (hydrangeaVisual && hydrangeaOption?.dataset.image) {
        hydrangeaVisual.style.setProperty('--clc-addon-image', `url("${hydrangeaOption.dataset.image}")`);
      }

      const lagurus = state.addons.find((addon) => addon.layer === 'lagurus');
      this.stage.dataset.lagurusColor = lagurus?.colorKey || 'bianco';
      const lagurusOption = this.querySelector('[data-clc-addon][data-layer="lagurus"]')
        ?.closest('[data-clc-addon-card]')
        ?.querySelector('[data-clc-addon-variant]')
        ?.selectedOptions?.[0];
      const lagurusVisual = this.querySelector('[data-clc-lagurus-visual]');
      if (lagurusVisual && lagurusOption?.dataset.image) {
        lagurusVisual.style.setProperty('--clc-addon-image', `url("${lagurusOption.dataset.image}")`);
      }

      const hypericum = state.addons.find((addon) => addon.layer === 'hypericum');
      this.stage.dataset.hypericumColor = hypericum?.colorKey || 'rosso';

      const bowRoses = state.addons.find((addon) => addon.layer === 'bow-roses');
      this.stage.dataset.bowRoseCount = bowRoses?.bowRoseCount || '1';
      this.stage.dataset.bowRoseSize = bowRoses?.flowerSize || 'piccola';
      this.stage.dataset.bowRoseColor = bowRoses?.colorKey || 'rosso';
      this.applyRoseSlotColors('.clc__bow-rose-slot', bowRoses);
      if (bowRoses?.image) this.stage.style.setProperty('--clc-active-bow-rose-image', `url("${bowRoses.image}")`);

      const bowRoseColorOption = this.querySelector('[data-clc-bow-rose-color]')?.selectedOptions?.[0];
      const bowRoseVisual = this.querySelector('[data-clc-bow-rose-visual]');
      if (bowRoseVisual && (bowRoseColorOption?.dataset.photo || bowRoseColorOption?.dataset.image)) {
        bowRoseVisual.style.setProperty('--clc-addon-image', `url("${bowRoseColorOption.dataset.photo || bowRoseColorOption.dataset.image}")`);
      }

      if (this.textPreviews.left) this.textPreviews.left.textContent = state.textLeft;
      if (this.textPreviews.right) this.textPreviews.right.textContent = state.textRight;
      if (this.countNodes.left) this.countNodes.left.textContent = String(this.textLength(state.textLeft));
      if (this.countNodes.right) this.countNodes.right.textContent = String(this.textLength(state.textRight));
      if (this.totalNode) this.totalNode.textContent = this.formatMoney(state.total);
      if (this.mobileTotalNode) this.mobileTotalNode.textContent = this.formatMoney(state.total);

      if (this.deliveryStatus) this.deliveryStatus.dataset.mode = state.requiresLocalDelivery ? 'local' : 'national';
      if (this.deliveryNational) this.deliveryNational.hidden = state.requiresLocalDelivery;
      if (this.deliveryLocal) this.deliveryLocal.hidden = !state.requiresLocalDelivery;
      if (this.localConfirmWrap) this.localConfirmWrap.hidden = !state.requiresLocalDelivery;
      if (this.localConfirm) {
        if (!state.requiresLocalDelivery && this.localConfirm.checked) this.localConfirm.checked = false;
        this.localConfirm.disabled = !state.requiresLocalDelivery;
      }
      const completionRequirements = this.completionRequirements(state);
      const rosesAvailable = !state.addons.some((addon) => !addon.catalogAvailable);
      const completionReady = completionRequirements.length === 0 && rosesAvailable;
      if (this.submitButton) {
        this.submitButton.disabled = this.isSubmitting || this.isReconciling || this.cartAttemptBlocksSubmit() || !completionReady;
      }
      if (this.submitLabel && !this.isSubmitting) {
        this.submitLabel.textContent = completionReady
          ? this.submitDefaultLabel
          : !rosesAvailable ? this.dataset.roseUnavailableLabel : this.message('complete_required');
      }
      if (this.completionNote) {
        this.completionNote.dataset.state = completionReady ? 'ready' : 'incomplete';
        this.completionNote.textContent = completionReady
          ? this.message('completion_ready')
          : !rosesAvailable ? this.dataset.roseUnavailableLabel : this.message('completion_missing', { REQUIREMENTS: this.formatRequirements(completionRequirements) });
      }

      const bowStyleLabels = { farfalla: this.displayChoice('farfalla'), coccarda: this.displayChoice('coccarda') };
      const widthLabels = { stretto: this.displayChoice('stretto'), largo: this.displayChoice('largo') };
      this.querySelector('[data-clc-summary-ribbon]').textContent = this.displayChoice(state.ribbon) || '—';
      this.querySelector('[data-clc-summary-bow]').textContent = this.message('bow_summary', { SHAPE: bowStyleLabels[state.bowStyle], WIDTH: widthLabels[state.width], COUNT: state.bands });
      this.querySelector('[data-clc-summary-addons]').textContent = state.addons.length
        ? state.addons.map((addon) => this.displayAddon(addon)).join(', ')
        : this.message('none');
      const textSummary = [];
      if (state.textLeft) textSummary.push(this.message('text_left', { TEXT: state.textLeft }));
      if (state.textRight) textSummary.push(this.message('text_right', { TEXT: state.textRight }));
      this.querySelector('[data-clc-summary-text]').textContent = textSummary.join(' · ') || this.message('none');
      this.querySelector('[data-clc-summary-lettering]').textContent = state.letteringColor === 'Extra'
        ? state.customLetteringColor
          ? this.message('extra_selected', { LABEL: state.letteringLabel, COLOUR: state.customLetteringColor })
          : this.message('extra_missing', { LABEL: state.letteringLabel })
        : state.letteringLabel || '—';
      this.querySelector('[data-clc-summary-circumference]').textContent = state.circumference || this.message('to_provide');
      this.querySelector('[data-clc-summary-graduation-date]').textContent = state.graduationDate || this.message('to_provide');

      if (this.statusNode?.dataset.kind !== 'persistent') this.statusNode.textContent = '';
      this.threeDRoot?.dispatchEvent(new CustomEvent('cl3d:update', {
        detail: { state: this.threeDState(state) },
      }));
      this.syncCartAttemptControls();
      if (this.draftTrackingReady && !this.suspendDraftSave && !this.isSubmitting) this.scheduleDraft();
    }

    dataKey(value) {
      return value.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    }

    storageProductKey() {
      return (this.dataset.productUrl || 'corona-dalloro').replace(/^\/(?:it|en)(?=\/products\/)/, '');
    }

    draftKey() {
      return `corone-laureae-configurator:draft:v2:${this.storageProductKey()}`;
    }

    legacyDraftKey() {
      return `corone-laureae-configurator:${this.dataset.sectionId || 'default'}`;
    }

    scheduleDraft() {
      window.clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = window.setTimeout(() => {
        this.draftSaveTimer = null;
        this.saveDraft(this.state());
      }, 400);
    }

    flushDraft() {
      if (!this.draftSaveTimer || this.suspendDraftSave) return;
      window.clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = null;
      this.saveDraft(this.state());
    }

    applyRoseSlotColors(selector, addon) {
      const filters = {
        azzurro: 'hue-rotate(183deg) saturate(.6) brightness(1.8)',
        'rosa-scuro': 'hue-rotate(342deg) saturate(.88) brightness(1.15)',
        fucsia: 'hue-rotate(314deg) saturate(1.05) brightness(1.35)',
        turchese: 'hue-rotate(168deg) saturate(.84) brightness(1.25)',
        bordeaux: 'saturate(.82) brightness(.66)',
        'viola-scuro': 'hue-rotate(264deg) saturate(.76) brightness(.69)',
        bourdeaux: 'saturate(.82) brightness(.55)',
        'rosa-confetto': 'saturate(.35) brightness(2.1)',
        lilla: 'hue-rotate(285deg) saturate(.3) brightness(1.7)',
        'blu-elettrico': 'hue-rotate(218deg) saturate(1.8) brightness(1.15)',
        gialle: 'hue-rotate(55deg) saturate(1.6) brightness(2)',
        nere: 'grayscale(1) brightness(.18)',
      };
      (this.stage.querySelectorAll?.(selector) || []).forEach((slot, index) => {
        const entry = addon?.roseMode === 'mix' ? addon.roseColors?.[index] : null;
        if (!entry) {
          slot.style.removeProperty('background-image');
          slot.style.removeProperty('filter');
          delete slot.dataset.roseColor;
          return;
        }
        slot.dataset.roseColor = entry.colour_id;
        if (entry.imageUrl) slot.style.setProperty('background-image', `url("${entry.imageUrl}")`);
        else slot.style.removeProperty('background-image');
        slot.style.setProperty('filter', `${filters[entry.colorKey] || 'saturate(.94) contrast(1.01) brightness(.97)'} drop-shadow(0 2px 2px rgba(43, 32, 29, .17))`);
      });
    }

    saveDraft(state) {
      try {
        const payload = {
          version: 2,
          ribbon: state.ribbon,
          bowStyle: state.bowStyle,
          width: state.width,
          bands: state.bands,
          textLeft: state.textLeft,
          textRight: state.textRight,
          letteringColor: state.letteringColor,
          customLetteringColor: state.customLetteringColor,
          legacyLetteringColor: this.legacyLetteringColor || '',
          circumference: state.circumference,
          graduationDate: state.graduationDate,
          details: state.details,
          addons: state.addons.map((addon) => ({
            layer: addon.layer,
            variantId: addon.variantId,
            babyMode: addon.babyMode,
            babyColorKey: addon.babyColorKey,
            roseCount: addon.roseCount,
            roseSize: addon.roseSize,
            colorKey: addon.colorKey,
            mode: addon.mode,
            level: addon.level,
            bowRoseCount: addon.bowRoseCount,
            flowerSize: addon.flowerSize,
            cartQuantity: addon.cartQuantity,
            ...(addon.roseMode === 'mix' ? { roseMode: 'mix', roseColors: addon.roseColors.map(entry => entry.colour_id) } : {}),
          })),
          savedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        };
        localStorage.setItem(this.draftKey(), JSON.stringify(payload));
        if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftSavedLabel;
        return true;
      } catch (_error) {
        if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftSaveErrorLabel;
        return false;
      }
    }

    restoreLetteringDraft(draft) {
      const radios = [...this.querySelectorAll('[data-clc-lettering-color]')];
      const savedColour = typeof draft.letteringColor === 'string' ? draft.letteringColor.trim() : '';
      const savedCustom = typeof draft.customLetteringColor === 'string' ? draft.customLetteringColor.slice(0, 36) : '';
      const savedLegacy = typeof draft.legacyLetteringColor === 'string' ? draft.legacyLetteringColor : '';
      this.legacyLetteringColor = '';
      if (this.customColorInput) this.customColorInput.value = savedCustom;
      if (!savedColour) return;
      const exact = radios.find(input => input.value === savedColour && !input.disabled);
      radios.forEach(input => { input.checked = input === exact; });
      if (exact) {
        if (savedColour === 'Extra' && savedLegacy) this.legacyLetteringColor = savedLegacy;
        return;
      }
      const extra = radios.find(input => input.value === 'Extra');
      if (extra) extra.checked = true;
      if (savedColour !== 'Extra' && this.customColorInput) this.customColorInput.value = savedColour.slice(0, 36);
      this.legacyLetteringColor = savedColour === 'Extra' ? savedLegacy || savedCustom || savedColour : savedColour;
    }

    restoreDraft() {
      let draft;
      try {
        const raw = localStorage.getItem(this.draftKey()) || localStorage.getItem(this.legacyDraftKey());
        if (!raw || raw.length > 18000) return;
        draft = JSON.parse(raw);
      } catch (_error) {
        return;
      }
      if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return;
      if (draft.version !== undefined && draft.version !== 2) return;
      const savedAt = Date.parse(draft.savedAt);
      const expiresAt = draft.version === 2 ? Date.parse(draft.expiresAt) : savedAt + 7 * 24 * 60 * 60 * 1000;
      if (!Number.isFinite(savedAt) || !Number.isFinite(expiresAt) || expiresAt <= savedAt || savedAt > Date.now() + 60000
        || expiresAt > savedAt + 7 * 24 * 60 * 60 * 1000 + 60000 || expiresAt <= Date.now()) {
        if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftExpiredLabel;
        return;
      }
      if (Array.isArray(draft.addons) && draft.addons.length > 20) return;

      const selectRadio = (selector, value) => {
        const input = [...this.querySelectorAll(selector)].find((candidate) => candidate.value === value);
        if (input && !input.disabled) input.checked = true;
      };

      selectRadio('[data-clc-ribbon]', draft.ribbon);
      selectRadio('[data-clc-bow-style]', draft.bowStyle || 'farfalla');
      selectRadio('[data-clc-width]', draft.width);
      selectRadio('[data-clc-bands]', draft.bands);
      this.restoreLetteringDraft(draft);

      const savedLeftText = typeof draft.textLeft === 'string' ? draft.textLeft : draft.text;
      if (this.textInputs.left && typeof savedLeftText === 'string') this.textInputs.left.value = this.truncateText(savedLeftText, 28);
      if (this.textInputs.right && typeof draft.textRight === 'string') this.textInputs.right.value = this.truncateText(draft.textRight, 28);
      if (this.circumferenceInput && typeof draft.circumference === 'string') {
        this.circumferenceInput.value = draft.circumference.slice(0, 20);
      }
      if (this.graduationDateInput && typeof draft.graduationDate === 'string') {
        this.graduationDateInput.value = draft.graduationDate.slice(0, 80);
      }
      if (this.detailsInput && typeof draft.details === 'string') {
        this.detailsInput.value = draft.details.slice(0, 600);
      }

      if (Array.isArray(draft.addons)) {
        draft.addons.forEach((savedAddon) => {
          if (!savedAddon || typeof savedAddon !== 'object' || Array.isArray(savedAddon)
            || typeof savedAddon.layer !== 'string' || !savedAddon.layer) return;
          const toggle = [...this.querySelectorAll('[data-clc-addon]')].find((candidate) => candidate.dataset.layer === savedAddon.layer);
          if (!toggle) return;
          toggle.checked = true;
          const card = toggle.closest('[data-clc-addon-card]');
          const select = card?.querySelector('[data-clc-addon-variant]');
          const roseSizeSelect = card?.querySelector('[data-clc-rose-size]');
          const roseModeSelect = card?.querySelector('[data-clc-rose-mode]');
          if (savedAddon.roseMode === 'mix' && !this.mixedRosesEnabled()) {
            this.disabledRoseMixDrafts ||= new Set();
            this.disabledRoseMixDrafts.add(savedAddon.layer);
          }
          if (roseModeSelect && savedAddon.roseMode === 'mix') {
            roseModeSelect.value = 'mix';
            this.roseMixSelections ||= { roses: [], 'bow-roses': [] };
            const savedCount = Number(savedAddon.layer === 'roses' ? savedAddon.roseCount : savedAddon.bowRoseCount);
            const restoredCount = Number.isInteger(savedCount) && savedCount > 0 && savedCount <= 10 ? savedCount : 0;
            this.roseMixSelections[savedAddon.layer] = Array.from({ length: restoredCount }, (_unused, index) => typeof savedAddon.roseColors?.[index] === 'string' ? savedAddon.roseColors[index] : '');
            const fields = card.querySelector('[data-clc-rose-mix-fields]');
            if (fields) { fields.replaceChildren(); delete fields.dataset.signature; }
          }
          if (roseSizeSelect) roseSizeSelect.value = this.normaliseRoseSize(savedAddon.roseSize);
          if (select) {
            const savedRoseOption = typeof savedAddon.roseCount === 'string' && savedAddon.roseCount
              ? [...select.options].find((option) => option.dataset.roseCount === savedAddon.roseCount)
              : null;
            const savedColorOption = typeof savedAddon.colorKey === 'string' && savedAddon.colorKey
              ? [...select.options].find((option) => option.dataset.colorKey === savedAddon.colorKey)
              : null;
            const savedQuantityOption = savedAddon.layer === 'sunflower'
              ? [...select.options].find(option => Number(option.dataset.cartQuantity) === Number(savedAddon.cartQuantity)) : null;
            const savedVariantOption = [...select.options].find((option) => option.value === savedAddon.variantId);
            if (savedQuantityOption) select.selectedIndex = [...select.options].indexOf(savedQuantityOption);
            else if (savedRoseOption) select.selectedIndex = [...select.options].indexOf(savedRoseOption);
            else if (savedColorOption) select.selectedIndex = [...select.options].indexOf(savedColorOption);
            else if (savedVariantOption) select.value = savedVariantOption.value;
          }

          const roseColorSelect = card?.querySelector('[data-clc-rose-color]');
          if (roseColorSelect && savedAddon.colorKey) {
            const savedColorOption = [...roseColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.colorKey);
            if (savedColorOption) roseColorSelect.value = savedColorOption.value;
          }
          if (roseSizeSelect) this.syncRoseOptions(savedAddon.roseCount);

          const bowRoseColorSelect = card?.querySelector('[data-clc-bow-rose-color]');
          if (bowRoseColorSelect && savedAddon.colorKey) {
            const savedColorOption = [...bowRoseColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.colorKey);
            if (savedColorOption) bowRoseColorSelect.value = savedColorOption.value;
          }
          if (bowRoseColorSelect) this.syncBowRoseColors();

          const babyColorSelect = card?.querySelector('[data-clc-baby-color]');
          if (babyColorSelect && savedAddon.babyColorKey) {
            const savedColorOption = [...babyColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.babyColorKey);
            if (savedColorOption) babyColorSelect.value = savedColorOption.value;
          }

          const babyModeSelect = card?.querySelector('[data-clc-baby-mode]');
          if (babyModeSelect) {
            const restoredBabyMode = savedAddon.babyMode === 'mista'
              ? 'sparsa'
              : savedAddon.babyMode === 'intervallata'
                ? 'a-ciuffi'
                : savedAddon.babyMode;
            if (['a-ciuffi', 'sparsa'].includes(restoredBabyMode)) babyModeSelect.value = restoredBabyMode;
          }

          card?.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
            field.disabled = false;
          });
        });
      }
      if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftRestoredLabel;
    }

    configurationCode() {
      const now = Date.now().toString(36).toUpperCase();
      const random = typeof window.crypto?.randomUUID === 'function'
        ? window.crypto.randomUUID().replace(/-/g, '').slice(0, 12).toUpperCase()
        : Math.random().toString(36).slice(2, 12).toUpperCase();
      return `CL-${now}-${random}`;
    }

    cartAttemptKey() {
      return `corone-laureae-configurator:cart-attempt:v1:${this.storageProductKey()}`;
    }

    readCartAttempt() {
      if (this.dataset.cartEnabled !== 'true') return null;
      try {
        const raw = localStorage.getItem(this.cartAttemptKey());
        if (!raw) return null;
        if (raw.length > 12000) return { status: 'UNKNOWN', code: '', expected: [] };
        const attempt = JSON.parse(raw);
        if (attempt?.version !== 1 || !/^CL-[A-Z0-9-]{4,80}$/.test(attempt.code || '')
          || !['UNKNOWN', 'REJECTED', 'PARTIAL', 'ACCEPTED', 'READY_TO_RETRY'].includes(attempt.status)
          || !Array.isArray(attempt.expected) || !attempt.expected.length || attempt.expected.length > 30
          || attempt.expected.some(item => !/^\d+$/.test(item.id || '') || !Number.isSafeInteger(item.quantity)
            || item.quantity < 1 || item.quantity > 100 || typeof item.base !== 'boolean')
          || attempt.expected.filter(item => item.base).length !== 1) {
          return { status: 'UNKNOWN', code: '', expected: [] };
        }
        return attempt;
      } catch {
        return { status: 'UNKNOWN', code: '', expected: [], storageError: true };
      }
    }

    saveCartAttempt(attempt) {
      this.cartAttempt = attempt;
      try {
        const payload = JSON.stringify(attempt);
        localStorage.setItem(this.cartAttemptKey(), payload);
        return localStorage.getItem(this.cartAttemptKey()) === payload;
      } catch {
        return false;
      }
    }

    clearAcceptedCartAttempt() {
      if (this.cartAttempt?.status !== 'ACCEPTED') return;
      const current = this.readCartAttempt();
      if (current && (current.code !== this.cartAttempt.code || current.status !== 'ACCEPTED')) {
        this.cartAttempt = current;
        return;
      }
      try {
        localStorage.removeItem(this.cartAttemptKey());
        if (!localStorage.getItem(this.cartAttemptKey())) this.cartAttempt = null;
      } catch {
      }
    }

    cartAttemptBlocksSubmit() {
      return Boolean(this.cartAttempt && this.cartAttempt.status !== 'READY_TO_RETRY');
    }

    syncCartAttemptControls() {
      const attempt = this.cartAttempt;
      if (this.cartCheckButton) {
        this.cartCheckButton.hidden = !attempt;
        this.cartCheckButton.disabled = this.isSubmitting || this.isReconciling || !attempt?.code;
      }
      if (this.cartReviewLink) this.cartReviewLink.hidden = !attempt;
      if (attempt && !this.isSubmitting && !this.isReconciling && this.statusNode?.dataset.kind !== 'persistent') {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.dataset.cartUnknownLabel;
      }
    }

    classifyCartAttempt(cart, attempt = this.cartAttempt) {
      if (!attempt?.code || !attempt.expected?.length || !Array.isArray(cart?.items)
        || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0
        || cart.items.some(item => !Number.isSafeInteger(item.quantity) || item.quantity < 1
          || !/^\d+$/.test(String(item.variant_id || item.id || '')))
        || cart.items.reduce((total, item) => total + item.quantity, 0) !== cart.item_count) return 'UNKNOWN';
      const lines = cart.items.filter(item => item.properties?.['Codice configurazione'] === attempt.code);
      if (!lines.length) return 'READY_TO_RETRY';
      const canonical = items => items.map(item => `${item.id}|${item.quantity}|${item.base}`).sort().join('\n');
      const observed = lines.map(item => ({
        id: String(item.variant_id || item.id),
        quantity: item.quantity,
        base: item.properties?._cl_config_base === 'true',
      }));
      if (lines.length !== attempt.expected.length || canonical(observed) !== canonical(attempt.expected)
        || lines.some(item => item.properties?._cl_config_group_version !== '1'
          || item.properties?._cl_config_group_size !== String(attempt.expected.length)
          || item.properties?._cl_config_unit_quantity !== String(item.quantity))) return 'PARTIAL';
      return 'ACCEPTED';
    }

    async reconcileCartAttempt() {
      if (!this.cartAttempt?.code || this.isSubmitting || this.isReconciling) return 'UNKNOWN';
      this.isReconciling = true;
      this.statusNode.dataset.kind = 'persistent';
      this.statusNode.textContent = this.dataset.cartCheckingLabel;
      this.update();
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Cart read failed');
        const cart = await response.json();
        const status = this.classifyCartAttempt(cart);
        this.saveCartAttempt({ ...this.cartAttempt, status, checkedAt: new Date().toISOString() });
        this.statusNode.textContent = status === 'ACCEPTED' ? this.dataset.cartAddedLabel
          : status === 'READY_TO_RETRY' ? this.dataset.cartRetryReadyLabel
            : status === 'PARTIAL' ? this.dataset.cartPartialLabel : this.dataset.cartUnknownLabel;
        return status;
      } catch {
        this.saveCartAttempt({ ...this.cartAttempt, status: 'UNKNOWN' });
        this.statusNode.textContent = this.dataset.cartUnknownLabel;
        return 'UNKNOWN';
      } finally {
        this.isReconciling = false;
        this.update();
      }
    }

    cartItems(state, code) {
      if (state.hasLettering && !/^\d+$/.test(String(state.letteringVariantId || ''))) {
        throw new Error(this.dataset.letteringMissingLabel);
      }
      if (state.addons.some(addon => addon.catalogAvailable === false
        || addon.roseMode === 'mix' && !this.mixedRosesEnabled()
        || addon.layer === 'roses' && (this.roseCatalog || []).some(variant => String(variant.id) === String(addon.variantId) && variant.options?.[0] === 'Mix di colori') && (!this.mixedRosesEnabled() || addon.roseMode !== 'mix')
        || addon.roseMode === 'mix' && !this.validRoseMixAddon(addon)
        || addon.layer === 'sunflower' && (!Number.isInteger(addon.cartQuantity) || addon.cartQuantity < 1 || addon.cartQuantity > 3)
        || addon.layer === 'bow-sunflower' && addon.cartQuantity !== 1)
        || state.addons.some(addon => addon.layer === 'bow-roses') && state.addons.some(addon => addon.layer === 'bow-sunflower')) {
        throw new Error(this.message('catalog_unavailable'));
      }
      const properties = {
        'Codice configurazione': code,
        'Colore nastro': state.ribbon,
        'Stile del fiocco': state.bowStyle,
        'Larghezza nastro': state.width,
        'Bande del nastro': state.bands,
        'Scritta nastro sinistro': state.textLeft || 'Nessuna',
        'Scritta nastro destro': state.textRight || 'Nessuna',
        'Colore scritte': state.letteringColor === 'Extra'
          ? state.customLetteringColor
            ? `Extra: ${state.customLetteringColor} (secondo disponibilità)`
            : 'Extra: da indicare (secondo disponibilità)'
          : state.letteringColor,
        'Decorazioni': state.addons.map((addon) => addon.detailLabel ? `${addon.title}: ${addon.detailLabel}` : addon.title).join(' · ') || 'Nessuna',
        'Circonferenza': state.circumference,
        'Giorno della Laurea': state.graduationDate,
        'Dettagli': state.details || 'Nessun dettaglio aggiuntivo',
      };
      for (const addon of state.addons.filter(item => item.roseMode === 'mix')) {
        properties[addon.layer === 'bow-roses' ? this.dataset.roseMixBowCartLabel || 'Colori rose sul fiocco' : this.dataset.roseMixCartLabel || 'Colori rose'] = addon.detailLabel;
      }

      if (state.requiresLocalDelivery) {
        properties['Disponibilità'] = 'Solo Latina — ritiro in negozio o consegna locale';
        properties._solo_latina = 'true';
        properties._conferma_solo_latina = 'sì';
      }

      const items = [{ id: String(state.baseVariantId || ''), quantity: 1, properties }];

      if (state.bands !== '2') {
        items.push({
          id: String(state.bandsVariantId || ''),
          quantity: 1,
          properties: { 'Codice configurazione': code },
        });
      }

      if (state.hasLettering) {
        items.push({
          id: String(state.letteringVariantId || ''),
          quantity: 1,
          properties: {
            'Codice configurazione': code,
            'Scritta nastro sinistro': state.textLeft || 'Nessuna',
            'Scritta nastro destro': state.textRight || 'Nessuna',
            'Colore scritte': properties['Colore scritte'],
          },
        });
      }

      state.addons.forEach((addon) => {
        items.push({
          id: String(addon.variantId || ''),
          quantity: addon.layer === 'roses' ? 1 : addon.cartQuantity || 1,
          properties: {
            'Codice configurazione': code,
            'Scelta nel configuratore': addon.detailLabel || addon.title,
            ...(addon.roseMode === 'mix' ? {
              [addon.layer === 'bow-roses' ? this.dataset.roseMixBowCartLabel || 'Colori rose sul fiocco' : this.dataset.roseMixCartLabel || 'Colori rose']: addon.detailLabel,
              _cl_rose_mix: JSON.stringify({ version: 1, placement: addon.layer === 'roses' ? 'crown' : 'bow', size: this.normaliseRoseSize(addon.layer === 'roses' ? addon.roseSize : addon.flowerSize) === 'piccola' ? 'Piccola' : 'Media', total: Number(addon.layer === 'roses' ? addon.roseCount : addon.bowRoseCount), rose_mix: addon.rose_mix }),
            } : {}),
            ...(addon.localOnly ? { _solo_latina: 'true', _componente_locale_obbligatorio: 'true' } : {}),
          },
        });
      });

      if (items.some((item) => !/^\d+$/.test(item.id))) {
        throw new Error(this.message('catalog_unlinked'));
      }

      items.forEach((item, index) => {
        item.properties._cl_config_group_version = '1';
        item.properties._cl_config_unit_quantity = String(item.quantity);
        item.properties._cl_config_group_size = String(items.length);
        if (index === 0) item.properties._cl_config_base = 'true';
      });

      return items;
    }

    validRoseMixAddon(addon) {
      if (!this.mixedRosesEnabled()) return false;
      if (!['roses', 'bow-roses'].includes(addon.layer)) return false;
      const total = Number(addon.layer === 'roses' ? addon.roseCount : addon.bowRoseCount);
      const size = this.normaliseRoseSize(addon.layer === 'roses' ? addon.roseSize : addon.flowerSize);
      const colors = addon.roseColors;
      if (!Array.isArray(colors) || colors.length !== total || !Array.isArray(addon.rose_mix) || addon.layer === 'bow-roses' && total !== 3) return false;
      const counts = new Map();
      for (const entry of colors) {
        if (!entry || typeof entry.colour_id !== 'string' || entry.colour_id !== entry.colorKey || !entry.label || entry.label === 'Mix di colori') return false;
        const key = entry.label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        if (key !== entry.colour_id) return false;
        const available = addon.layer === 'roses' ? this.resolveRoseVariant(entry.label, total, size) : (this.roseCatalog || []).find(variant => variant.available === true && variant.options?.[0] === entry.label && this.normaliseRoseSize(variant.options?.[2]) === size);
        if (!this.isLegacyRoseColour(key, entry.label) && (available?.available !== true || addon.layer === 'roses' && available.price !== addon.price)) return false;
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      const canonical = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b, 'en')).map(([colour_id, count]) => ({ colour_id, count }));
      if (JSON.stringify(canonical) !== JSON.stringify(addon.rose_mix)) return false;
      if (addon.layer === 'roses') {
        const variant = this.resolveRoseVariant('Mix di colori', total, size);
        if (variant?.available !== true || String(variant.id) !== addon.variantId || variant.price !== addon.price) return false;
      } else {
        const composition = this.querySelector('[data-clc-bow-rose-color]')?.closest('[data-clc-addon-card]')?.querySelector('[data-clc-addon-variant]')?.selectedOptions?.[0];
        if (!composition || composition.disabled || String(composition.value) !== addon.variantId
          || Number(composition.dataset.price) !== addon.price || Number(composition.dataset.bowRoseCount) !== total
          || this.normaliseRoseSize(composition.dataset.flowerSize) !== size) return false;
      }
      return true;
    }

    async submit() {
      if (this.isSubmitting || this.isReconciling) return;
      this.cartAttempt = this.readCartAttempt() || this.cartAttempt;
      if (this.cartAttemptBlocksSubmit()) {
        if (!this.cartAttempt.code) {
          this.statusNode.dataset.kind = 'persistent';
          this.statusNode.textContent = this.cartAttempt.storageError
            ? this.dataset.cartStorageErrorLabel : this.dataset.cartUnknownLabel;
          this.update();
          return;
        }
        await this.reconcileCartAttempt();
        return;
      }
      if (this.cartAttempt?.status === 'READY_TO_RETRY'
        && await this.reconcileCartAttempt() !== 'READY_TO_RETRY') return;
      const state = this.state();

      if (!this.validateConfiguration(state)) return;

      if (state.requiresLocalDelivery && !this.localConfirm?.checked) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.message('confirm_local');
        this.localConfirm?.focus();
        return;
      }

      const code = this.cartAttempt?.code || this.configurationCode();
      const draftSaved = this.saveDraft(state);

      if (!this.cartEnabled) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = draftSaved
          ? this.message('test_saved', { CODE: code })
          : this.dataset.draftSaveErrorLabel
            || this.message('draft_save_error');
        return;
      }

      if (!state.baseVariantId) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.message('base_unlinked');
        return;
      }

      let items;
      try {
        items = this.cartItems(state, code);
      } catch (error) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = error.message;
        return;
      }
      const attempt = {
        version: 1,
        code,
        status: 'UNKNOWN',
        createdAt: this.cartAttempt?.createdAt || new Date().toISOString(),
        expected: items.map(item => ({ id: item.id, quantity: item.quantity, base: item.properties._cl_config_base === 'true' })),
      };
      if (!this.saveCartAttempt(attempt)) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.dataset.cartStorageErrorLabel;
        this.update();
        return;
      }

      const originalLabel = this.submitButton.querySelector('span')?.textContent || '';
      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.submitButton.querySelector('span').textContent = this.message('adding');
      this.statusNode.dataset.kind = '';
      this.statusNode.textContent = '';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          const rejected = [400, 404, 422].includes(response.status) && (typeof error.description === 'string' || typeof error.message === 'string');
          this.saveCartAttempt({ ...attempt, status: rejected ? 'REJECTED' : 'UNKNOWN' });
          throw new Error(rejected ? `${this.dataset.cartRejectedLabel} ${String(error.description || error.message).slice(0, 300)}` : this.dataset.cartUnknownLabel);
        }

        const added = await response.json();
        const addedCount = Array.isArray(added?.items) ? added.items.reduce((total, item) => total + item.quantity, 0) : -1;
        if (this.classifyCartAttempt({ items: added?.items, item_count: addedCount }, attempt) !== 'ACCEPTED') {
          throw new Error(this.dataset.cartUnknownLabel);
        }
        this.saveCartAttempt({ ...attempt, status: 'ACCEPTED' });

        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.message('added_redirect');
        window.setTimeout(() => window.location.assign(`${root}cart`), 500);
      } catch (error) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.cartAttempt?.status === 'REJECTED'
          ? error.message : this.dataset.cartUnknownLabel;
        this.isSubmitting = false;
        this.submitButton.querySelector('span').textContent = originalLabel;
        this.update();
      }
    }

    validateConfiguration(state) {
      if (!this.mixedRosesEnabled() && state.addons.some(addon => addon.roseMode === 'mix')) {
        if (this.statusNode) {
          this.statusNode.dataset.kind = 'validation';
          this.statusNode.textContent = this.dataset.roseMixPendingLabel;
        }
        return false;
      }
      const requiredFields = [
        {
          input: this.circumferenceInput,
          value: state.circumference,
          message: this.message('validation_circumference'),
        },
        {
          input: this.graduationDateInput,
          value: state.graduationDate,
          message: this.message('validation_graduation'),
        },
      ];

      if (state.letteringColor === 'Extra') {
        requiredFields.push({
          input: this.customColorInput,
          value: state.customLetteringColor,
          message: this.message('validation_extra'),
        });
      }

      requiredFields.forEach(({ input, value, message }) => {
        input?.setCustomValidity(value ? '' : message);
      });

      const invalid = requiredFields.find(({ input }) => input && !input.checkValidity());
      if (invalid) {
        if (this.statusNode) {
          this.statusNode.dataset.kind = 'validation';
          this.statusNode.textContent = invalid.message;
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        invalid.input.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        invalid.input.focus({ preventScroll: true });
        invalid.input.reportValidity();
        return false;
      }

      const numericId = (value) => /^\d+$/.test(String(value || ''));
      const invalidAddon = state.addons?.find((addon) => !numericId(addon.variantId) || addon.catalogAvailable === false);
      const hasInvalidCatalogLink = !numericId(state.baseVariantId)
        || (state.bands !== '2' && !numericId(state.bandsVariantId))
        || (state.hasLettering && !numericId(state.letteringVariantId))
        || Boolean(invalidAddon);

      if (hasInvalidCatalogLink) {
        if (this.statusNode) {
          this.statusNode.dataset.kind = 'validation';
          this.statusNode.textContent = this.message('catalog_unavailable');
        }
        const invalidControl = invalidAddon
          ? this.querySelector(`[data-clc-addon][data-layer="${invalidAddon.layer}"]`)
          : this.querySelector('[data-clc-ribbon]:checked');
        invalidControl?.focus();
        return false;
      }

      return true;
    }

    confirmReset() {
      if (this.resetConfirmationResolve || !this.resetDialog || typeof this.resetDialog.showModal !== 'function') {
        return Promise.resolve(false);
      }
      if (this.isPreviewOpen) this.closePreview();
      return new Promise(resolve => {
        this.resetConfirmationResolve = resolve;
        try {
          this.resetDialog.showModal();
          this.resetCancel?.focus({ preventScroll: true });
        } catch {
          this.finishResetConfirmation(false);
        }
      });
    }

    finishResetConfirmation(confirmed) {
      const resolve = this.resetConfirmationResolve;
      if (!resolve) return;
      this.resetConfirmationResolve = null;
      if (this.resetDialog?.open) this.resetDialog.close();
      this.resetButton?.focus({ preventScroll: true });
      resolve(confirmed === true);
    }

    async resetConfiguration() {
      if (this.isSubmitting || this.isReconciling || this.resetConfirmationResolve) return;

      const changedChoice = [...this.querySelectorAll('input[type="radio"], input[type="checkbox"]')]
        .some((input) => input.checked !== input.defaultChecked);
      const changedSelect = [...this.querySelectorAll('select')].some((select) => {
        const defaultIndex = [...select.options].findIndex((option) => option.defaultSelected);
        return select.selectedIndex !== (defaultIndex >= 0 ? defaultIndex : 0);
      });
      const hasEnteredText = [
        this.textInputs.left,
        this.textInputs.right,
        this.customColorInput,
        this.circumferenceInput,
        this.graduationDateInput,
        this.detailsInput,
      ].some((input) => Boolean(input?.value.trim()));

      if ((changedChoice || changedSelect || hasEnteredText || this.cartAttempt?.status === 'ACCEPTED')
        && !await this.confirmReset()) return;

      this.clearAcceptedCartAttempt();
      this.suspendDraftSave = true;
      window.clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = null;
      this.roseMixSelections = { roses: [], 'bow-roses': [] };
      this.disabledRoseMixDrafts = new Set();
      this.legacyLetteringColor = '';
      this.querySelectorAll('[data-clc-rose-mix-fields]').forEach(container => { container.replaceChildren(); delete container.dataset.signature; });

      try {
        localStorage.removeItem(this.draftKey());
        localStorage.removeItem(this.legacyDraftKey());
      } catch (_error) {
      }

      this.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        input.checked = input.defaultChecked;
        input.setCustomValidity('');
      });
      this.querySelectorAll('select').forEach((select) => {
        const defaultIndex = [...select.options].findIndex((option) => option.defaultSelected);
        select.selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;
      });
      [
        this.textInputs.left,
        this.textInputs.right,
        this.customColorInput,
        this.circumferenceInput,
        this.graduationDateInput,
        this.detailsInput,
      ].forEach((input) => {
        if (!input) return;
        input.value = '';
        input.setCustomValidity('');
      });
      this.querySelectorAll('[data-clc-addon-card]').forEach((card) => {
        const toggle = card.querySelector('[data-clc-addon]');
        card.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !toggle?.checked;
        });
      });

      this.syncRoseOptions('3');
      this.update();
      this.suspendDraftSave = false;
      if (this.draftStatus) this.draftStatus.textContent = this.dataset.draftEmptyLabel;
      if (this.statusNode) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = this.cartAttempt
          ? this.dataset.cartUnknownLabel : 'Configurazione azzerata. Puoi ricominciare dal colore del nastro.';
      }
      this.querySelector('[data-clc-ribbon]')?.focus();
    }

    setPreviewBackgroundInert() {
      this.previewBackgroundState = [];
      let activeBranch = this.previewColumn;

      while (activeBranch && activeBranch !== document.body) {
        const parent = activeBranch.parentElement;
        if (!parent) break;

        [...parent.children].forEach((sibling) => {
          if (sibling === activeBranch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
          this.previewBackgroundState.push({
            element: sibling,
            inert: Boolean(sibling.inert),
          });
          sibling.inert = true;
        });

        activeBranch = parent;
      }
    }

    restorePreviewBackground() {
      (this.previewBackgroundState || []).forEach(({ element, inert }) => {
        if (!element?.isConnected) return;
        element.inert = inert;
      });
      this.previewBackgroundState = [];
    }

    openPreview() {
      if (!this.previewColumn || this.isPreviewOpen || !window.matchMedia('(max-width: 749px)').matches) return;
      this.isPreviewOpen = true;
      this.previewPreviouslyFocused = document.activeElement;
      this.previewScrollY = window.scrollY;
      this.previewColumn.classList.add('is-mobile-open');
      this.previewColumn.setAttribute('role', 'dialog');
      this.previewColumn.setAttribute('aria-modal', 'true');
      this.previewOpenButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('clc-preview-modal-open');
      this.previewCloseButton?.focus({ preventScroll: true });
      this.setPreviewBackgroundInert();
      this.syncMobileBar();
      window.requestAnimationFrame(() => this.previewCloseButton?.focus({ preventScroll: true }));
    }

    closePreview({ focusTarget } = {}) {
      if (!this.isPreviewOpen || !this.previewColumn) return;
      this.isPreviewOpen = false;
      this.previewColumn.classList.remove('is-mobile-open');
      this.previewColumn.removeAttribute('role');
      this.previewColumn.removeAttribute('aria-modal');
      this.previewOpenButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('clc-preview-modal-open');
      this.restorePreviewBackground();
      this.syncMobileBar();
      const restoreTarget = focusTarget?.isConnected
        ? focusTarget
        : this.previewPreviouslyFocused?.isConnected
          ? this.previewPreviouslyFocused
          : this.previewOpenButton;
      const scrollY = this.previewScrollY;
      window.requestAnimationFrame(() => {
        if (Number.isFinite(scrollY)) window.scrollTo(0, scrollY);
        restoreTarget?.focus({ preventScroll: true });
      });
    }
  }

  customElements.define('cl-configurator', CoroneLaureaeConfigurator);
})();
